import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ClockCircleOutlined,
  DollarOutlined,
  TeamOutlined,
  ProjectOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { Spin } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart,
} from 'recharts';
import { useReportAnalytics } from '@/app/time-tracking/_shared/hooks';
import { cn } from '@/libs/utils';

// Muted, sophisticated palette
const COLORS = [
  '#6b8db5',
  '#7da87d',
  '#c4956a',
  '#9b8bb4',
  '#5ea5a5',
  '#b08d8d',
  '#8ca0c4',
  '#a3b56b',
  '#c9a0c9',
  '#7bbfbf',
  '#8e9cc0',
  '#b5c48a',
  '#c29d7e',
  '#a58db5',
  '#6db5a8',
];

function StatCard({ icon, label, value, sub, color = 'text-gray-700' }) {
  return (
    <div className="flex flex-col gap-1 rounded-lg border bg-white p-4">
      <div className="flex items-center gap-2 text-xs text-gray-400">
        {icon}
        <span>{label}</span>
      </div>
      <div className={`text-lg font-semibold ${color}`}>{value}</div>
      {sub && <div className="text-xs text-gray-400">{sub}</div>}
    </div>
  );
}

function ChartCard({ title, children, className = '' }) {
  return (
    <div className={cn('rounded-lg border bg-white p-4', className)}>
      <h4 className="mb-3 text-sm font-medium text-gray-500">{title}</h4>
      {children}
    </div>
  );
}

export default function ReportAnalytics({
  dateFrom,
  dateTo,
  company,
  isAdmin,
}) {
  const { t } = useTranslation('forms');

  const queryParams = useMemo(() => {
    const p = { company };
    if (dateFrom) p.dateFrom = dateFrom;
    if (dateTo) p.dateTo = dateTo;
    return p;
  }, [dateFrom, dateTo, company]);

  const { data: response, isLoading } = useReportAnalytics(
    company ? queryParams : {}
  );

  const analytics = response?.data;
  const totals = analytics?.totals;
  const byMember = analytics?.byMember || [];

  // Pie chart data — soft green + warm amber
  const pieData = useMemo(() => {
    if (!totals) return [];
    return [
      { name: t('billable'), value: totals.billableHours, color: '#7da87d' },
      {
        name: t('nonBillable'),
        value: totals.nonBillableHours,
        color: '#c4956a',
      },
    ].filter((d) => d.value > 0);
  }, [totals, t]);

  // Format daily trend dates
  const trendData = useMemo(() => {
    const trend = analytics?.dailyTrend || [];
    return trend.map((d) => {
      const dt = new Date(d.date);
      return {
        ...d,
        label: `${String(dt.getDate()).padStart(2, '0')}.${String(dt.getMonth() + 1).padStart(2, '0')}`,
      };
    });
  }, [analytics?.dailyTrend]);

  // Top projects (limit 10 for chart readability)
  const projectChartData = useMemo(
    () => (analytics?.byProject || []).slice(0, 10),
    [analytics?.byProject]
  );

  if (!company) return null;
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spin size="large" />
      </div>
    );
  }
  if (!analytics || !totals) return null;

  const avgPerDay =
    totals.activeDays > 0
      ? (totals.totalHours / totals.activeDays).toFixed(1)
      : 0;

  const tooltipStyle = {
    contentStyle: {
      borderRadius: 8,
      border: '1px solid #e5e7eb',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      fontSize: 12,
    },
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Summary stats */}
      <div
        className={cn(
          'grid gap-3',
          isAdmin
            ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6'
            : 'grid-cols-2 md:grid-cols-4'
        )}
      >
        <StatCard
          icon={<ClockCircleOutlined />}
          label={t('totalTracked')}
          value={`${totals.totalHours}h`}
          sub={avgPerDay > 0 ? `~${avgPerDay}h / ${t('day')}` : undefined}
        />
        {isAdmin && (
          <StatCard
            icon={<DollarOutlined />}
            label={t('billable')}
            value={`${totals.billableHours}h`}
            sub={
              totals.totalHours > 0
                ? `${Math.round((totals.billableHours / totals.totalHours) * 100)}% ${t('ofTotal')}`
                : undefined
            }
            color="text-emerald-600"
          />
        )}
        {isAdmin && (
          <StatCard
            icon={<DollarOutlined />}
            label={t('totalAmount')}
            value={totals.totalAmount.toLocaleString('sr-RS', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            color="text-slate-600"
          />
        )}
        <StatCard
          icon={<ProjectOutlined />}
          label={t('activeProjects')}
          value={totals.activeProjects}
        />
        <StatCard
          icon={<TeamOutlined />}
          label={isAdmin ? t('activeMembers') : t('activeDays')}
          value={isAdmin ? totals.activeMembers : totals.activeDays}
        />
        <StatCard
          icon={<FileTextOutlined />}
          label={t('entries')}
          value={totals.entryCount}
        />
      </div>

      {/* Charts row */}
      <div
        className={cn(
          'grid gap-4',
          isAdmin ? 'lg:grid-cols-3' : 'lg:grid-cols-2'
        )}
      >
        {/* Project comparison bar chart */}
        {projectChartData.length > 0 && (
          <ChartCard
            title={t('hoursPerProject')}
            className={isAdmin ? 'lg:col-span-2' : ''}
          >
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={projectChartData}
                margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#f0f0f0"
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: '#94a3b8' }}
                  interval={0}
                  angle={-30}
                  textAnchor="end"
                  height={60}
                  axisLine={{ stroke: '#e5e7eb' }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#94a3b8' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  {...tooltipStyle}
                  formatter={(value, name) => [
                    `${value}h`,
                    name === 'billableHours'
                      ? t('billable')
                      : name === 'nonBillableHours'
                        ? t('nonBillable')
                        : t('total'),
                  ]}
                />
                {isAdmin ? (
                  <>
                    <Bar
                      dataKey="billableHours"
                      stackId="a"
                      name="billableHours"
                      radius={[0, 0, 0, 0]}
                    >
                      {projectChartData.map((entry, i) => (
                        <Cell
                          key={i}
                          fill={entry.color || COLORS[i % COLORS.length]}
                        />
                      ))}
                    </Bar>
                    <Bar
                      dataKey="nonBillableHours"
                      stackId="a"
                      name="nonBillableHours"
                      radius={[3, 3, 0, 0]}
                    >
                      {projectChartData.map((entry, i) => (
                        <Cell
                          key={i}
                          fill={entry.color || COLORS[i % COLORS.length]}
                          fillOpacity={0.35}
                        />
                      ))}
                    </Bar>
                  </>
                ) : (
                  <Bar dataKey="totalHours" radius={[3, 3, 0, 0]}>
                    {projectChartData.map((entry, i) => (
                      <Cell
                        key={i}
                        fill={entry.color || COLORS[i % COLORS.length]}
                      />
                    ))}
                  </Bar>
                )}
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {/* Billable pie chart (admin only) */}
        {isAdmin && pieData.length > 0 && (
          <ChartCard title={t('billableBreakdown')}>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={65}
                  outerRadius={105}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) =>
                    `${name} ${(percent * 100).toFixed(0)}%`
                  }
                  labelLine={false}
                  stroke="none"
                >
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip {...tooltipStyle} formatter={(value) => `${value}h`} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        )}
      </div>

      {/* Daily trend */}
      {trendData.length > 1 && (
        <ChartCard title={t('dailyTrend')}>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart
              data={trendData}
              margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#f0f0f0"
                vertical={false}
              />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                axisLine={{ stroke: '#e5e7eb' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#94a3b8' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                {...tooltipStyle}
                formatter={(value, name) => [
                  `${value}h`,
                  name === 'totalHours'
                    ? t('total')
                    : name === 'billableHours'
                      ? t('billable')
                      : name,
                ]}
              />
              <Area
                type="monotone"
                dataKey="totalHours"
                stroke="#6b8db5"
                fill="#6b8db5"
                fillOpacity={0.08}
                strokeWidth={2}
                name="totalHours"
              />
              {isAdmin && (
                <Area
                  type="monotone"
                  dataKey="billableHours"
                  stroke="#7da87d"
                  fill="#7da87d"
                  fillOpacity={0.08}
                  strokeWidth={2}
                  name="billableHours"
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* Per-member breakdown (admin only) */}
      {isAdmin && byMember.length > 0 && (
        <ChartCard title={t('hoursPerMember')}>
          <ResponsiveContainer
            width="100%"
            height={Math.max(200, byMember.length * 35)}
          >
            <BarChart
              data={byMember}
              layout="vertical"
              margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#f0f0f0"
                horizontal={false}
              />
              <XAxis
                type="number"
                tick={{ fontSize: 11, fill: '#94a3b8' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 11, fill: '#64748b' }}
                width={120}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                {...tooltipStyle}
                formatter={(value, name) => [
                  name === 'amount'
                    ? value.toLocaleString('sr-RS', {
                        minimumFractionDigits: 2,
                      })
                    : `${value}h`,
                  name === 'billableHours'
                    ? t('billable')
                    : name === 'totalHours'
                      ? t('total')
                      : t('amount'),
                ]}
              />
              <Bar
                dataKey="billableHours"
                fill="#7da87d"
                name="billableHours"
                radius={[0, 3, 3, 0]}
              />
              <Bar
                dataKey="totalHours"
                fill="#6b8db5"
                name="totalHours"
                radius={[0, 3, 3, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* Revenue by project (admin only) */}
      {isAdmin && (analytics?.byProject || []).some((p) => p.amount > 0) && (
        <ChartCard title={t('revenueByProject')}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={projectChartData.filter((p) => p.amount > 0)}
              margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#f0f0f0"
                vertical={false}
              />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: '#94a3b8' }}
                interval={0}
                angle={-30}
                textAnchor="end"
                height={60}
                axisLine={{ stroke: '#e5e7eb' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#94a3b8' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                {...tooltipStyle}
                formatter={(value) =>
                  value.toLocaleString('sr-RS', { minimumFractionDigits: 2 })
                }
              />
              <Bar
                dataKey="amount"
                fill="#9b8bb4"
                name={t('amount')}
                radius={[3, 3, 0, 0]}
              >
                {projectChartData
                  .filter((p) => p.amount > 0)
                  .map((entry, i) => (
                    <Cell
                      key={i}
                      fill={entry.color || COLORS[i % COLORS.length]}
                    />
                  ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}
    </div>
  );
}
